import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface DocumentGrant {
  id: string;
  document_id: string;
  listing_id: string;
  granted_to_user_id: string;
  granted_by_user_id: string;
  access_request_id?: string;
  expires_at?: string;
  created_at: string;
}

export const useDocumentGrants = (listingId?: string) => {
  const [grants, setGrants] = useState<DocumentGrant[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchGrants = async () => {
    if (!listingId) {
      setLoading(false);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('document_access_grants' as any)
        .select('*')
        .eq('listing_id', listingId)
        .eq('granted_to_user_id', user.id);

      if (error) throw error;
      setGrants((data as any) || []);
    } catch (error) {
      console.error('Error fetching document grants:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasAccess = (documentId: string): boolean => {
    return grants.some(
      g =>
        g.document_id === documentId &&
        (!g.expires_at || new Date(g.expires_at) > new Date())
    );
  };

  const getGrantedDocumentIds = (): string[] => {
    return grants
      .filter(g => !g.expires_at || new Date(g.expires_at) > new Date())
      .map(g => g.document_id);
  };

  useEffect(() => {
    fetchGrants();

    if (!listingId) return;

    // Real-time subscription for document grants
    const channel = supabase
      .channel(`doc-grants-${listingId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'document_access_grants',
          filter: `listing_id=eq.${listingId}`
        },
        (payload) => {
          console.log('Document grant change:', payload);
          fetchGrants();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [listingId]);

  return {
    grants,
    loading,
    hasAccess,
    getGrantedDocumentIds,
    refetch: fetchGrants
  };
};
